import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Search } from "lucide-react";
import { formatarDataAbertura } from "@/lib/formatarDataAbertura";

const OBJETO_MAX_LEN = 80;

function truncar(texto) {
  if (!texto || typeof texto !== "string") return "—";
  const t = texto.trim();
  if (t.length <= OBJETO_MAX_LEN) return t;
  return t.slice(0, OBJETO_MAX_LEN) + "…";
}

function formatDataHoraPregao(edital) {
  const data = edital.data_abertura || edital.dataAbertura;
  const hora = edital.hora_abertura || edital.horaAbertura;
  if (!data || data === "Não localizado" || data === "null") return "—";
  const dataStr = formatarDataAbertura(data);
  if (!dataStr) return "—";
  if (hora && String(hora).trim() && String(hora) !== "Não localizado") {
    return `${dataStr} ${String(hora).substring(0, 5)}`;
  }
  return dataStr;
}

/** Status de extração / processamento (não confundir com status_aprovacao). */
function statusProcessamentoEdital(e) {
  if (e == null || typeof e !== "object") return null;
  const v = e.status ?? e.Status;
  return v != null && String(v).trim() !== "" ? String(v).trim() : null;
}

function statusAprovacaoEdital(e) {
  if (e == null || typeof e !== "object") return null;
  const v = e.status_aprovacao ?? e.statusAprovacao;
  return v != null && String(v).trim() !== "" ? String(v).trim() : null;
}

function normStatus(s) {
  if (s == null || s === "") return "";
  return String(s).trim().toLowerCase();
}

/** Inclui edital na lista filtrada por «concluído» (pronto para uso em propostas e similares). */
function editalPassaFiltroConcluido(e) {
  if (normStatus(statusProcessamentoEdital(e)) === "concluido") return true;
  if (normStatus(statusAprovacaoEdital(e)) === "concluido") return true;
  return false;
}

function timestampAtualizacao(edital) {
  const raw =
    edital.updated_at ??
    edital.updatedAt ??
    edital.created_at ??
    edital.createdAt ??
    null;
  if (raw == null) return 0;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : 0;
}

function matchBusca(edital, termo) {
  if (!termo || !termo.trim()) return true;
  const lower = termo.toLowerCase().trim();
  const campos = [
    edital.instituicao,
    edital.titulo_licitacao,
    edital.nome_arquivo,
    edital.numero_pregao,
    edital.numeroPregao,
    edital.numero_processo,
    edital.numeroProcesso,
    edital.objeto,
    edital.sistema_eletronico,
    edital.sistemaEletronico,
    edital.data_abertura,
    edital.dataAbertura,
    edital.hora_abertura,
    edital.horaAbertura,
  ]
    .filter(Boolean)
    .map((s) => String(s).toLowerCase());
  return campos.some((c) => c.includes(lower));
}

const TEXTO_AJUDA_PADRAO =
  'Busque por instituição, número do pregão, processo ou objeto e clique em "Selecionar" para vincular à proposta.';

export default function SelecionarPregaoModal({
  open,
  onOpenChange,
  editais = [],
  editalSelecionadoId,
  onSelecionar,
  statusFiltro = "concluido",
  textoAjuda = TEXTO_AJUDA_PADRAO,
  /** Quantidade de pregões por página na tabela. */
  itensPorPagina = 20,
  /** Quando a lista ainda está a carregar (ex.: GET /api/editais). */
  carregandoLista = false,
  /** Mensagem de erro ao obter editais (evita confundir com «zero pregões»). */
  erroLista = null,
  /** Com `statusFiltro="todos"`, ordena extração concluída antes dos restantes. */
  priorizarExtracaoConcluida = false,
}) {
  const [termoBusca, setTermoBusca] = useState("");
  const [pagina, setPagina] = useState(1);

  const lista = Array.isArray(editais) ? editais : [];
  const filtrados = useMemo(() => {
    let porStatus;
    if (statusFiltro === "todos") {
      porStatus = lista;
    } else if (statusFiltro === "concluido") {
      porStatus = lista.filter((e) => editalPassaFiltroConcluido(e));
    } else {
      const alvo = normStatus(statusFiltro);
      porStatus = lista.filter((e) => {
        return normStatus(statusProcessamentoEdital(e)) === alvo || normStatus(statusAprovacaoEdital(e)) === alvo;
      });
    }
    if (!termoBusca.trim()) return porStatus;
    return porStatus.filter((e) => matchBusca(e, termoBusca));
  }, [lista, termoBusca, statusFiltro]);

  /** Mais recentes primeiro; opcionalmente pregões com extração concluída no topo. */
  const filtradosOrdenados = useMemo(() => {
    const arr = [...filtrados];
    if (priorizarExtracaoConcluida && statusFiltro === "todos") {
      arr.sort((a, b) => {
        const ca = editalPassaFiltroConcluido(a) ? 1 : 0;
        const cb = editalPassaFiltroConcluido(b) ? 1 : 0;
        if (ca !== cb) return cb - ca;
        return timestampAtualizacao(b) - timestampAtualizacao(a);
      });
    } else {
      arr.sort((a, b) => timestampAtualizacao(b) - timestampAtualizacao(a));
    }
    return arr;
  }, [filtrados, priorizarExtracaoConcluida, statusFiltro]);

  const totalPaginas = Math.max(1, Math.ceil(filtradosOrdenados.length / itensPorPagina));
  const paginaEfetiva = Math.min(Math.max(1, pagina), totalPaginas);
  const inicio = (paginaEfetiva - 1) * itensPorPagina;
  const filaPagina = filtradosOrdenados.slice(inicio, inicio + itensPorPagina);

  useEffect(() => {
    if (open) setPagina(1);
  }, [open]);

  useEffect(() => {
    setPagina(1);
  }, [termoBusca]);

  useEffect(() => {
    setPagina((p) => Math.min(Math.max(1, p), totalPaginas));
  }, [totalPaginas]);

  const handleSelecionar = (edital) => {
    onSelecionar(edital);
    onOpenChange(false);
    setTermoBusca("");
    setPagina(1);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex flex-col w-[95vw] max-w-4xl max-h-[85vh] overflow-hidden p-6 gap-4">
        <div className="flex-shrink-0 min-w-0 space-y-2">
          <DialogHeader>
            <DialogTitle className="pr-8">Consultar e selecionar pregão</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 break-words">{textoAjuda}</p>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 shrink-0" />
            <Input
              placeholder="Buscar por instituição, pregão, processo, objeto..."
              value={termoBusca}
              onChange={(e) => setTermoBusca(e.target.value)}
              className="pl-10 min-w-0 w-full"
            />
          </div>
        </div>
        <div className="border rounded-lg min-h-0 min-w-0 flex-1 flex flex-col overflow-hidden">
          <div className="overflow-auto min-h-0 flex-1 min-w-0">
            <Table className="table-fixed w-full">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[20%]">Instituição</TableHead>
                <TableHead className="w-[10%] whitespace-nowrap">Nº Pregão</TableHead>
                <TableHead className="w-[10%] whitespace-nowrap">Processo</TableHead>
                <TableHead className="w-[12%] whitespace-nowrap">Data e Hora</TableHead>
                <TableHead className="w-[20%]">Objeto (resumido)</TableHead>
                <TableHead className="w-[10%] whitespace-nowrap">Sistema</TableHead>
                <TableHead className="w-[10%] text-right">Ação</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {carregandoLista ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                    Carregando pregões…
                  </TableCell>
                </TableRow>
              ) : erroLista ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-amber-800 bg-amber-50/80 py-8 px-4">
                    {erroLista}
                  </TableCell>
                </TableRow>
              ) : filtradosOrdenados.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                    {lista.length === 0
                      ? "Nenhum pregão cadastrado nesta empresa. Importe um edital em Análise ou use a opção \"Não associar a um pregão\" no formulário da proposta."
                      : "Nenhum resultado para a busca."}
                  </TableCell>
                </TableRow>
              ) : (
                filaPagina.map((edital) => (
                  <TableRow
                    key={edital.id}
                    className={
                      editalSelecionadoId === edital.id
                        ? "bg-blue-50 border-blue-200"
                        : ""
                    }
                  >
                    <TableCell className="font-medium break-words align-top max-w-0 overflow-hidden" title={edital.instituicao || edital.titulo_licitacao || edital.nome_arquivo || ""}>
                      <span className="inline-flex flex-wrap items-center gap-1.5">
                        <span>
                          {edital.instituicao ||
                            edital.titulo_licitacao ||
                            edital.nome_arquivo ||
                            "—"}
                        </span>
                        {editalPassaFiltroConcluido(edital) ? (
                          <span className="shrink-0 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-900">
                            Extração OK
                          </span>
                        ) : null}
                      </span>
                    </TableCell>
                    <TableCell className="align-top overflow-hidden text-ellipsis whitespace-nowrap max-w-0" title={edital.numero_pregao || edital.numeroPregao || ""}>
                      {edital.numero_pregao || edital.numeroPregao || "—"}
                    </TableCell>
                    <TableCell className="align-top overflow-hidden text-ellipsis whitespace-nowrap max-w-0" title={edital.numero_processo || edital.numeroProcesso || ""}>
                      {edital.numero_processo || edital.numeroProcesso || "—"}
                    </TableCell>
                    <TableCell className="align-top overflow-hidden text-ellipsis whitespace-nowrap max-w-0 text-slate-600" title={formatDataHoraPregao(edital)}>
                      {formatDataHoraPregao(edital)}
                    </TableCell>
                    <TableCell className="text-slate-600 break-words align-top max-w-0 overflow-hidden" title={edital.objeto || ""}>
                      {truncar(edital.objeto)}
                    </TableCell>
                    <TableCell className="align-top overflow-hidden text-ellipsis whitespace-nowrap text-slate-600 max-w-0" title={edital.sistema_eletronico || edital.sistemaEletronico || ""}>
                      {edital.sistema_eletronico || edital.sistemaEletronico || "—"}
                    </TableCell>
                    <TableCell className="text-right align-top whitespace-nowrap">
                      <Button
                        size="sm"
                        variant={editalSelecionadoId === edital.id ? "default" : "outline"}
                        onClick={() => handleSelecionar(edital)}
                      >
                        Selecionar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>
          {!carregandoLista && !erroLista && filtradosOrdenados.length > 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-t border-slate-200 bg-slate-50/80 text-sm text-slate-600 shrink-0">
              <span>
                {filtradosOrdenados.length}{" "}
                {filtradosOrdenados.length === 1 ? "pregão" : "pregões"}
                {` · linhas ${inicio + 1}–${Math.min(inicio + itensPorPagina, filtradosOrdenados.length)}`}
              </span>
              {totalPaginas > 1 ? (
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={paginaEfetiva <= 1}
                    onClick={() => setPagina((p) => Math.max(1, p - 1))}
                  >
                    Anterior
                  </Button>
                  <span className="tabular-nums whitespace-nowrap">
                    Página {paginaEfetiva} de {totalPaginas}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={paginaEfetiva >= totalPaginas}
                    onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
                  >
                    Próxima
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
